import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react'

import {
  type InsightsPanel,
  type InsightsPanels,
  type PanelRow,
} from './panels'
import {
  anchorDay,
  inWindowPhrase,
  one,
  panelRows,
  rawPanel,
  resolvedRange,
  type Series,
  series,
  windowSuffix,
} from './period'
import { DEFAULT_PERIOD, type InsightsPeriod } from './periods'

export type PeriodApi = {
  period: InsightsPeriod
  setPeriod: (period: InsightsPeriod) => void
  panels: InsightsPanels
  raw: (name: InsightsPanel) => PanelRow[]
  panel: (name: InsightsPanel) => PanelRow[]
  one: (name: InsightsPanel) => PanelRow
  series: (weekly: InsightsPanel, daily: InsightsPanel) => Series
  anchorDay: string | null
  resolvedRange: string
  windowSuffix: string
  inWindowPhrase: string
}

const PeriodContext = createContext<PeriodApi | null>(null)

/**
 * The selected window and every panel already filtered to it. Sections read
 * through this rather than the payload directly, so changing the window
 * re-renders every card — including the numbers inside prose — with no
 * per-card subscription to leak.
 */
export const PeriodProvider = ({
  panels,
  children,
}: {
  panels: InsightsPanels
  children: ReactNode
}) => {
  const [period, setPeriod] = useState<InsightsPeriod>(DEFAULT_PERIOD)

  const api = useMemo<PeriodApi>(
    () => ({
      period,
      setPeriod,
      panels,
      raw: name => rawPanel(panels, name),
      panel: name => panelRows(panels, name, period),
      one: name => one(panels, name, period),
      series: (weekly, daily) => series(panels, period, weekly, daily),
      anchorDay: anchorDay(panels, period),
      resolvedRange: resolvedRange(panels, period),
      windowSuffix: windowSuffix(period),
      inWindowPhrase: inWindowPhrase(period),
    }),
    [panels, period]
  )

  return <PeriodContext.Provider value={api}>{children}</PeriodContext.Provider>
}

export const usePeriod = (): PeriodApi => {
  const api = useContext(PeriodContext)
  if (!api) throw new Error('usePeriod used outside PeriodProvider')
  return api
}
